import { type QueryKey, queryOptions } from "@tanstack/react-query";
import type { ZodUnknown } from "zod";
import { request, type Search } from "./api";
import {
  type AnyEndpoint,
  type BodyOf,
  type Endpoint,
  fill,
  type PathParams,
  type ResponseOf,
  type WireList,
} from "./endpoints";

/**
 * The typed way to call the console API: an endpoint from lib/endpoints.ts, its path params,
 * and (for mutations) its body. Responses come back typed as the endpoint declares them.
 */

/** Calls any endpoint: `apiRequest(endpoints.updateOnboarding, { orgId }, { body })`. */
export function apiRequest<E extends AnyEndpoint>(
  endpoint: E,
  params: PathParams<E["path"]>,
  ...[options]: undefined extends BodyOf<E>
    ? [options?: { body?: BodyOf<E>; search?: Search; signal?: AbortSignal }]
    : [options: { body: BodyOf<E>; search?: Search; signal?: AbortSignal }]
): Promise<ResponseOf<E>> {
  return request<ResponseOf<E>>(fill(endpoint.path, params), {
    method: endpoint.method,
    body: options?.body,
    search: options?.search,
    signal: options?.signal,
  });
}

/**
 * TanStack Query options for a GET. The key is `["api", pattern, params, search]`, so a
 * mutation can invalidate everything under a pattern (`apiKey(endpoints.onboarding, { orgId
 * })`) and an organization switch never serves another organization's cache.
 */
export function apiQuery<E extends Endpoint<"GET">>(
  endpoint: E,
  params: PathParams<E["path"]>,
  search?: Search,
) {
  // Typed as the general QueryKey so every page query fits one `PageQuery<T>` shape.
  const queryKey: QueryKey = ["api", endpoint.path, params, search ?? null];
  return queryOptions({
    queryKey,
    queryFn: ({ signal }) =>
      request<ResponseOf<E>>(fill(endpoint.path, params), { search, signal }),
  });
}

/** The key prefix shared by every query on an endpoint (optionally narrowed to some params). */
export function apiKey<E extends AnyEndpoint>(endpoint: E, params?: PathParams<E["path"]>) {
  return params === undefined ? (["api", endpoint.path] as const) : ["api", endpoint.path, params];
}

/** The platform's list envelope (core's `listOf`), with items the caller does not inspect. */
export type ListEnvelope = WireList<ZodUnknown>;

/** True for a list envelope; used where a page only needs to know whether a list is empty. */
export function isListEnvelope(value: unknown): value is ListEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    "object" in value &&
    value.object === "list" &&
    "data" in value &&
    Array.isArray(value.data)
  );
}
